import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  useTheme,
  Alert,
  Select,
  MenuItem,
  FormControl
} from '@mui/material'
import {
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  Language as LanguageIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material'
import toast from 'react-hot-toast'
import PageContentLayout from '../components/layout/PageContentLayout'

const Settings = () => {
  const theme = useTheme()
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      push: false,
      sms: false,
      attendance: true,
      grades: true,
      fees: true
    },
    appearance: {
      theme: 'light',
      compact: false,
      sidebar: true
    },
    security: {
      twoFactor: false,
      sessionTimeout: 30,
      passwordExpiry: 90
    },
    school: {
      name: 'EduOK School',
      address: '123 Education Street, City, State 12345',
      phone: '+1-555-0123',
      email: 'info@eduokschool.com',
      website: 'www.eduokschool.com'
    }
  })

  const [userProfile, setUserProfile] = useState({
    firstName: 'Admin',
    lastName: 'User',
    email: 'admin@school.com',
    phone: '+1-555-0124',
    role: 'Administrator'
  })

  const handleSettingChange = (category, setting, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value
      }
    }))
  }

  const handleProfileChange = (field, value) => {
    setUserProfile(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSaveSettings = () => {
    toast.success('Settings saved successfully')
  }

  const handleResetSettings = () => {
    toast.success('Settings reset to default')
  }

  const handleSaveProfile = () => {
    toast.success('Profile updated successfully')
  }

  return (
    <PageContentLayout
      title="Settings"
      subtitle="Manage your account settings and system preferences"
      breadcrumbs={[
        { label: 'Settings', icon: <SettingsIcon sx={{ color: '#ffffff', fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
    >
        <Grid container spacing={3}>
          {/* User Profile */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PersonIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    User Profile
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={userProfile.firstName}
                      onChange={(e) => handleProfileChange('firstName', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={userProfile.lastName}
                      onChange={(e) => handleProfileChange('lastName', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={userProfile.email}
                      onChange={(e) => handleProfileChange('email', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={userProfile.phone}
                      onChange={(e) => handleProfileChange('phone', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Role"
                      value={userProfile.role}
                      disabled
                      size="small"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveProfile}
                    sx={{ px: 3 }}
                  >
                    Save Profile
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => setUserProfile({
                      firstName: 'Admin',
                      lastName: 'User',
                      email: 'admin@school.com',
                      phone: '+1-555-0124',
                      role: 'Administrator'
                    })}
                  >
                    Reset
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* School Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <SchoolIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    School Information
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="School Name"
                      value={settings.school.name}
                      onChange={(e) => handleSettingChange('school', 'name', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={settings.school.address}
                      onChange={(e) => handleSettingChange('school', 'address', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={settings.school.phone}
                      onChange={(e) => handleSettingChange('school', 'phone', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={settings.school.email}
                      onChange={(e) => handleSettingChange('school', 'email', e.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Website"
                      value={settings.school.website}
                      onChange={(e) => handleSettingChange('school', 'website', e.target.value)}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Notifications */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <NotificationsIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Notification Settings
                  </Typography>
                </Box>
                
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Email Notifications"
                      secondary="Receive notifications via email"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings.notifications.email}
                        onChange={(e) => handleSettingChange('notifications', 'email', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Push Notifications"
                      secondary="Receive push notifications in browser"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings.notifications.push}
                        onChange={(e) => handleSettingChange('notifications', 'push', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="SMS Notifications"
                      secondary="Receive notifications via SMS"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings.notifications.sms}
                        onChange={(e) => handleSettingChange('notifications', 'sms', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider sx={{ my: 1 }} />
                  <ListItem>
                    <ListItemText 
                      primary="Attendance Alerts"
                      secondary="Get notified about attendance issues"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings.notifications.attendance}
                        onChange={(e) => handleSettingChange('notifications', 'attendance', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Grade Updates"
                      secondary="Get notified when grades are posted"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings.notifications.grades}
                        onChange={(e) => handleSettingChange('notifications', 'grades', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Fee Reminders"
                      secondary="Get notified about fee due dates"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings.notifications.fees}
                        onChange={(e) => handleSettingChange('notifications', 'fees', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Appearance */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PaletteIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Appearance
                  </Typography>
                </Box>
                
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Theme"
                      secondary="Choose your preferred theme"
                    />
                    <ListItemSecondaryAction>
                      <FormControl size="small">
                        <Select
                          value={settings.appearance.theme}
                          onChange={(e) => handleSettingChange('appearance', 'theme', e.target.value)}
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="light">Light</MenuItem>
                          <MenuItem value="dark">Dark</MenuItem>
                          <MenuItem value="auto">Auto</MenuItem>
                        </Select>
                      </FormControl>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Compact Mode"
                      secondary="Use compact layout for better space utilization"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings.appearance.compact}
                        onChange={(e) => handleSettingChange('appearance', 'compact', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Sidebar"
                      secondary="Show navigation sidebar"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings.appearance.sidebar}
                        onChange={(e) => handleSettingChange('appearance', 'sidebar', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Security */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <SecurityIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Security Settings
                  </Typography>
                </Box>
                
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Two-Factor Authentication"
                      secondary="Add an extra layer of security to your account"
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings.security.twoFactor}
                        onChange={(e) => handleSettingChange('security', 'twoFactor', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Session Timeout"
                      secondary="Auto-logout after inactivity (minutes)"
                    />
                    <ListItemSecondaryAction>
                      <TextField
                        type="number"
                        value={settings.security.sessionTimeout}
                        onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
                        size="small"
                        sx={{ width: 80 }}
                        inputProps={{ min: 5, max: 120 }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Password Expiry"
                      secondary="Days until password expires"
                    />
                    <ListItemSecondaryAction>
                      <TextField
                        type="number"
                        value={settings.security.passwordExpiry}
                        onChange={(e) => handleSettingChange('security', 'passwordExpiry', parseInt(e.target.value))}
                        size="small"
                        sx={{ width: 80 }}
                        inputProps={{ min: 30, max: 365 }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Language & Region */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <LanguageIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Language & Region
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <Select
                        value="en"
                        label="Language"
                        disabled
                      >
                        <MenuItem value="en">English (US)</MenuItem>
                        <MenuItem value="es">Spanish</MenuItem>
                        <MenuItem value="fr">French</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <Select
                        value="us"
                        label="Region"
                        disabled
                      >
                        <MenuItem value="us">United States</MenuItem>
                        <MenuItem value="uk">United Kingdom</MenuItem>
                        <MenuItem value="ca">Canada</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <Select
                        value="est"
                        label="Time Zone"
                        disabled
                      >
                        <MenuItem value="est">Eastern Time (EST/EDT)</MenuItem>
                        <MenuItem value="cst">Central Time (CST/CDT)</MenuItem>
                        <MenuItem value="mst">Mountain Time (MST/MDT)</MenuItem>
                        <MenuItem value="pst">Pacific Time (PST/PDT)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              sx={{ px: 4, py: 1.5, fontWeight: 600 }}
            >
              Save All Settings
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={handleResetSettings}
              sx={{ px: 4, py: 1.5, fontWeight: 600 }}
            >
              Reset to Default
            </Button>
          </motion.div>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mt: 4 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Some settings may require a system restart to take effect. 
            Changes to security settings will be applied immediately.
          </Typography>
        </Alert>
    </PageContentLayout>
  )
}

export default Settings
